import React, { useState, useEffect } from 'react';
import { clipAudioApi } from '../../../services/api';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  Animation, 
  Image, 
  CheckCircle,
  PlayArrow,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';

function Step4Animation({ projectId, projectData, onAnimationComplete, onRefreshProject }) {
  const images = projectData?.images || [];
  const [showImages, setShowImages] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const handleGenerate = async () => {
    setIsGenerating(true);
    
    try {
      const result = await clipAudioApi.executeStep(projectId, projectData.current_step);
      console.log('Generation successful:', result);
      // Refresh project data to get latest state
      if (onRefreshProject) {
        onRefreshProject();
      }
    } catch (error) {
      console.error('Error generating animation:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleValidate = async () => {
    setIsValidating(true);
    
    try {
      const result = await clipAudioApi.validateStep(projectId, projectData.current_step);
      console.log('Validation successful:', result);
      // Refresh project data to get latest state
      if (onRefreshProject) {
        onRefreshProject();
      }
      // Handle successful validation
      if (onAnimationComplete) {
        onAnimationComplete();
      }
    } catch (error) {
      console.error('Error validating step:', error);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Box sx={{ p: 2.5 }}>
      {/* Display Theme */}
      {(() => {
        const theme = projectData?.lyric?.theme || projectData?.theme;
        const themeText = typeof theme === 'string' ? theme : (theme?.theme || '');
        return themeText && (
          <Alert severity="info" sx={{ mb: 2.5, py: 1 }}>
            <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600 }}>
              🎯 Thème: {themeText}
            </Typography>
          </Alert>
        );
      })()}

             <Card sx={{ mb: 2.5, borderRadius: 3, bgcolor: '#F8F9FA' }}>
        <CardContent sx={{ p: 3 }}>
                     <Typography variant="h4" component="h3" sx={{ 
             textAlign: 'center', 
             mb: 2, 
             fontWeight: 'bold',
             color: '#2C3E50'
           }}>
             🎬 Étape 4: Animation des Images
           </Typography>

          {/* Images Reference */}
          <Box sx={{
            bgcolor: '#F8F9FA',
            borderRadius: 2,
            p: 2,
            mb: 2.5,
            border: '1px solid #E1E8ED'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ color: '#7F8C8D' }}>
                  Étape 4: Animation des Images
                </Typography>
                {/* Theme Display */}
                {(() => {
                  const theme = projectData?.lyric?.theme || projectData?.theme;
                  const themeText = typeof theme === 'string' ? theme : (theme?.theme || '');
                  return themeText && (
                    <Typography variant="body1" sx={{ color: '#2C3E50', fontWeight: 'bold' }}>
                      Thème: {themeText}
                    </Typography>
                  );
                })()}
              </Box>
              {images && images.length > 0 && (
                <Button
                  onClick={() => setShowImages(!showImages)}
                  startIcon={showImages ? <ExpandLess /> : <ExpandMore />}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: '#8E44AD',
                    color: '#8E44AD',
                    '&:hover': {
                      borderColor: '#7D3C98',
                      backgroundColor: '#F8F4FD'
                    }
                  }}
                >
                  {showImages ? 'Masquer' : 'Voir images'}
                </Button>
              )}
            </Box>

            {/* Images Display */}
            {images && images.length > 0 && showImages && (
              <Box sx={{
                bgcolor: '#F8F4FD',
                borderRadius: 1,
                p: 1.5,
                mt: 1.5,
                border: '1px solid #8E44AD'
              }}>
                <Typography variant="body2" sx={{
                  color: '#2C3E50',
                  fontWeight: 'bold',
                  mb: 1
                }}>
                  {images.length} scènes avec images disponibles:
                </Typography>
                {images.map((imageGroup, index) => (
                  <Box key={index} sx={{ mb: 2, pl: 1 }}>
                    <Typography variant="body2" sx={{
                      color: '#7F8C8D',
                      fontSize: '0.85rem',
                      lineHeight: 1.3,
                      fontWeight: 'bold'
                    }}>
                      Scène {imageGroup.scene.number}: {imageGroup.scene.title}
                    </Typography>
                    <Typography variant="caption" sx={{
                      color: '#7F8C8D',
                      fontSize: '0.75rem',
                      fontStyle: 'italic',
                      display: 'block',
                      mb: 1
                    }}>
                      {imageGroup.scene.one_line}
                    </Typography>
                                         <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                       {imageGroup.images.map((imageObj, imgIndex) => {
                         const imageUrl = typeof imageObj === 'string' ? imageObj : imageObj.url;
                         return (
                         <Box key={imgIndex} sx={{
                           width: '200px',
                           height: '150px',
                           borderRadius: '12px',
                           overflow: 'hidden',
                           border: '3px solid #8E44AD',
                           bgcolor: '#E8F4FD',
                           boxShadow: '0 4px 12px rgba(142, 68, 173, 0.3)',
                           transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                           '&:hover': {
                             transform: 'scale(1.05)',
                             boxShadow: '0 6px 20px rgba(142, 68, 173, 0.4)'
                           }
                         }}>
                           <img 
                             src={imageUrl} 
                             alt={`Image ${imgIndex + 1} - ${imageGroup.scene.title}`}
                             style={{
                               width: '100%',
                               height: '100%',
                               objectFit: 'cover'
                             }}
                           />
                         </Box>
                         );
                       })}
                     </Box>
                  </Box>
                ))}
              </Box>
            )}
            {images && images.length > 0 ? (
              <Typography variant="body2" sx={{
                color: '#7F8C8D',
                fontStyle: 'italic',
                mt: 1
              }}>
                "Créons maintenant des animations fluides entre ces images..."
              </Typography>
            ) : (
              <Box sx={{
                bgcolor: '#FFEBEE',
                borderRadius: 2,
                p: 2.5,
                border: '2px solid #EF5350',
                mt: 1
              }}>
                <Typography variant="h6" sx={{
                  fontWeight: 'bold',
                  color: '#C62828',
                  mb: 1
                }}>
                  ⚠️ Aucune image disponible
                </Typography>
                <Typography variant="body2" sx={{
                  color: '#C62828',
                  fontStyle: 'italic'
                }}>
                  Veuillez d'abord générer des images à l'étape 3.
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ 
            bgcolor: '#F8F9FA', 
            borderRadius: 2, 
            p: 3,
            textAlign: 'center',
            border: '2px solid #3498DB'
          }}>
            <Box sx={{ fontSize: '4rem', mb: 2 }}>🚧</Box>
            <Typography variant="h5" sx={{ 
              fontWeight: 'bold', 
              color: '#2C3E50',
              mb: 1.5
            }}>
              Fonctionnalité en cours de développement
            </Typography>
            <Typography variant="body1" sx={{ 
              color: '#7F8C8D',
              lineHeight: 1.6,
              mb: 2
            }}>
              L'animation des images sera bientôt disponible.<br />
              Cette étape permettra de créer des transitions fluides entre les images générées.
            </Typography>
            
            <Chip
              icon={<PlayArrow />}
              label="Animation Preview"
              color="warning"
              variant="outlined"
              sx={{ 
                fontSize: '1rem',
                fontWeight: 'bold',
                borderWidth: '2px'
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={isGenerating}
              startIcon={isGenerating ? <CircularProgress size={20} /> : <Animation />}
              sx={{
                background: 'linear-gradient(135deg, #8E44AD 0%, #7D3C98 100%)',
                borderRadius: '50px',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                minWidth: '150px',
                boxShadow: '0 8px 25px rgba(142, 68, 173, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7D3C98 0%, #6C3483 100%)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 30px rgba(142, 68, 173, 0.6)'
                },
                '&:disabled': {
                  background: '#BDBDBD',
                  transform: 'none',
                  boxShadow: 'none'
                }
              }}
            >
              {isGenerating ? 'Génération...' : 'Générer'}
            </Button>
            
            <Button
              variant="contained"
              onClick={handleValidate}
              disabled={isValidating}
              startIcon={isValidating ? <CircularProgress size={20} /> : <CheckCircle />}
              sx={{
                background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                borderRadius: '50px',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                minWidth: '150px',
                boxShadow: '0 8px 25px rgba(39, 174, 96, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 30px rgba(39, 174, 96, 0.6)'
                },
                '&:disabled': {
                  background: '#BDBDBD',
                  transform: 'none',
                  boxShadow: 'none'
                }
              }}
            >
              {isValidating ? 'Validation...' : 'Valider'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default Step4Animation;
